import { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigation } from "react-router-dom";

import { usePrimaryNavItems } from "../usePrimaryNavItems";

import { Item } from "./item/Item";

/** App navigation menu, shown as a bottom bar on phones. */
export const Footer = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const navRef = useRef<HTMLDivElement>(null);
  // Keep the last primary selection for nested/detail routes without a primary tab.
  const lastSettledPrimaryKey = useRef<string | undefined>(undefined);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const items = usePrimaryNavItems();
  const itemsSignature = items.map(({ key, to }) => `${key}:${to}`).join("|");
  const pendingPathname = navigation.state === "idle" ? undefined : navigation.location?.pathname;
  const pendingItem = items.find(
    (item) => pendingPathname === item.to || pendingPathname?.startsWith(`${item.to}/`),
  );
  const settledItem = items.find(
    (item) =>
      item.isActiveOverride ??
      (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)),
  );
  if (!pendingItem && settledItem) lastSettledPrimaryKey.current = settledItem.key;

  useLayoutEffect(() => {
    if (!navRef.current) return;

    const activeLink = navRef.current.querySelector('[data-active="true"]') as HTMLElement;

    if (!activeLink) {
      setIndicatorStyle({ left: 0, width: 0 });
      return;
    }

    const containerRect = navRef.current.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    // Add extra padding to make indicator larger than button content
    const extraPadding = 20;

    setIndicatorStyle({
      left: linkRect.left - containerRect.left - extraPadding,
      width: linkRect.width + extraPadding * 2,
    });
  }, [location.pathname, itemsSignature, pendingPathname]);

  return (
    <div
      ref={navRef}
      className="relative z-40 flex w-full items-center justify-between bg-white px-[16px] pb-[calc(9px_+_env(safe-area-inset-bottom))] pt-[7px] shadow-footerNavigation"
    >
      {/* Active-tab indicator */}
      <div
        className="absolute top-[7px] h-[44px] rounded-full bg-brand-color/10"
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
        }}
      />

      {items.map((item) => (
        <Item
          key={item.key}
          svg={item.svg}
          to={item.to}
          label={item.label}
          isActiveOverride={
            pendingItem
              ? pendingItem.key === item.key
              : (item.isActiveOverride ??
                (settledItem
                  ? settledItem.key === item.key
                  : lastSettledPrimaryKey.current === item.key))
          }
          intentPreload={item.intentPreload}
        />
      ))}
    </div>
  );
};
